/**
 * ON HOLD — the card you deliberately took out of the work, and when it comes back.
 *
 * The other half of client/src/crm/waiting.mjs. That one asks whether the ball is in
 * our court; this asks whether we have put the ball down on purpose. The two can never
 * both be true: a suppressed company is served no actions at all
 * (getBoardChips in src/actions/index.js), so `waitingAction` is null for everything
 * this returns a state for.
 *
 * Nothing new is computed here either. `chip.suppression` is already in
 * GET /board-chips, built by the one enforcement point every surface goes through
 * (src/actions/scope.js), which is what stops a card reading "back in 2 weeks" while a
 * cleanup job writes the same company off. This file decides WHICH suppressions the
 * card should show as a hold, and the words it says.
 *
 * TWO OF THE FOUR KINDS, and the split is between a decision and a derivation:
 *
 *   snoozed  — "come back to this on the 21st". A hold.
 *   ignored  — "this is the wrong kind of company", with a reason. Also a hold: it is
 *              Gabriel's own decision, it is what the Muted list already groups with a
 *              snooze, and until now an ignored company's card was indistinguishable
 *              from a live one.
 *
 *   chased            — off the queues for three days because we emailed them this
 *                       morning. NOT a hold: that is live work we just did, and
 *                       hatching every card we touched today would shade out half the
 *                       board for three days at a time.
 *   auto_close_pending — the weekly sweep is proposing we write this off. NOT a hold
 *                       either: it is on its way OFF the board rather than parked on
 *                       it, and the card already carries the ghosted tag that says so.
 *
 * Both of those are `derived: true` — nobody chose them — which is the same line the
 * Muted list draws when it decides which rows get an Un-mute button.
 */

/** The snooze windows the server can resolve, in the order the menus offer them.
 *  Moved here from FollowUpsPage so the board's hold menu and the follow-up row's
 *  snooze menu name the same five things. The keys are SNOOZE_PRESETS in
 *  src/db/followUps.js and test/board-hold.test.js guards that they still match —
 *  the client build refuses imports from outside client/src, which is why this is a
 *  copy at all (see test/stage-order.test.js for the same arrangement). */
export const SNOOZE_LABELS = {
  tomorrow: "Tomorrow morning",
  "3_days": "In 3 days",
  next_monday: "Next Monday",
  "2_weeks": "In 2 weeks",
  until_reply: "After their next reply",
};

const MS_DAY = 86400000;

/** Whole calendar days from today to a "YYYY-MM-DD", or null. */
function daysUntil(until, today = new Date()) {
  if (!until) return null;
  const m = String(until).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const then = Date.UTC(+m[1], +m[2] - 1, +m[3]);
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((then - now) / MS_DAY);
}

/**
 * "back in 2 weeks" — the mockup's words.
 *
 * Deliberately ROUGH above a fortnight, because the exact day is printed beside it:
 * the pill answers "roughly when" at a glance and the date answers "which day". That
 * is the same division of labour the card's other timing line makes.
 */
function backLabel(until, today) {
  const days = daysUntil(until, today);
  if (days === null) return "on hold";
  if (days <= 1) return "back tomorrow";
  if (days < 14) return `back in ${days} days`;
  if (days < 90) return `back in ${Math.round(days / 7)} weeks`;
  const months = Math.round(days / 30);
  return `back in ${months} month${months === 1 ? "" : "s"}`;
}

/**
 * The hold this card is under, or null.
 *
 * Returns `label` (the pill), `until` (the date to print, or null when there is no day
 * to name), `kind` and `note` — everything the card renders, decided once here so the
 * pill and the date cannot describe different decisions.
 */
export function holdState(chip, today = new Date()) {
  const s = chip && chip.suppression;
  if (!s || s.derived) return null;
  if (s.kind !== "snoozed" && s.kind !== "ignored") return null;

  if (s.kind === "ignored") {
    // The reason is the point of an ignore — it is what the lead agent learns from —
    // so it is what the pill says. "ignored" alone would be a state with no content.
    const label = s.reason_label ? `ignored · ${s.reason_label}` : "ignored";
    // `forever` has no day to print. The Muted list says "never"; on a card that word
    // beside a company name reads like a verdict on the company rather than on our
    // own queue, so the card prints nothing and the pill carries the whole meaning.
    return { kind: "ignored", label, until: s.forever ? null : s.until || null, note: s.note || null };
  }

  // A reply-conditional snooze has a backstop date, and the card does NOT print it:
  // the ball is theirs, so "when they reply" is the honest answer and a date beside it
  // would read as a promise about a day nobody named. Same call the Muted list makes.
  if (s.until_reply) {
    return { kind: "snoozed", label: "back when they reply", until: null, note: s.note || null };
  }

  return { kind: "snoozed", label: backLabel(s.until, today), until: s.until || null, note: s.note || null };
}
